import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ViewToggle from "@/components/ViewToggle";
import {
  useAllAppConfigs,
  useDeleteAppConfig,
} from "@/hooks/useAppConfigurations";
import { useApplications } from "@/hooks/useApplications";
import { AppConfigCard } from "@/components/applications/config/AppConfigCard";
import { AppConfigRow } from "@/components/applications/config/AppConfigRow";
import { AppConfigSnapshot } from "@/services/appConfigApi";
import {
  SortControls,
  type SortOption,
} from "@/components/configurations/SortControls";
import {
  usePersistentSort,
  compareSortString,
  toSortTime,
} from "@/hooks/usePersistentSort";

const STORAGE_KEY = "appConfigsViewPreference";
const SORT_STORAGE_KEY = "appConfigsSortPreference";

type SortField = "name" | "app" | "keys" | "updated";

const SORT_OPTIONS: ReadonlyArray<SortOption<SortField>> = [
  { value: "updated", label: "Last updated" },
  { value: "name", label: "Configuration name" },
  { value: "app", label: "App name" },
  { value: "keys", label: "Key count" },
];

const VALID_FIELDS: SortField[] = SORT_OPTIONS.map((o) => o.value);

export default function AppConfigurationsPage() {
  const navigate = useNavigate();
  const { data: configs = [], isLoading } = useAllAppConfigs();
  const { applications } = useApplications();
  const deleteConfig = useDeleteAppConfig();

  const [view, setView] = useState<"grid" | "list">(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "grid" || saved === "list" ? saved : "list";
  });
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<
    "all" | "linked" | "external"
  >("all");
  const { sort, setField, setDirection } = usePersistentSort<SortField>(
    SORT_STORAGE_KEY,
    { field: "updated", direction: "desc" },
    VALID_FIELDS,
  );

  const [deleteTarget, setDeleteTarget] = useState<AppConfigSnapshot | null>(
    null,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, view);
  }, [view]);

  const appsByBundleId = useMemo(() => {
    const map = new Map<string, (typeof applications)[number]>();
    for (const app of applications) {
      if (app.bundleId) map.set(app.bundleId, app);
    }
    return map;
  }, [applications]);

  const linkedBundleIds = useMemo(
    () => new Set(applications.map((a) => a.bundleId).filter(Boolean) as string[]),
    [applications],
  );

  const enriched = useMemo(
    () =>
      configs.map((config) => {
        const app = appsByBundleId.get(config.bundleId);
        return {
          config,
          appName: app?.name ?? config.bundleId,
          iconUrl: app?.icon || undefined,
          linked: linkedBundleIds.has(config.bundleId),
        };
      }),
    [configs, appsByBundleId, linkedBundleIds],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(({ config, appName, linked }) => {
      if (sourceFilter === "linked" && !linked) return false;
      if (sourceFilter === "external" && linked) return false;
      if (!q) return true;
      return (
        appName.toLowerCase().includes(q) ||
        config.configName.toLowerCase().includes(q) ||
        config.bundleId.toLowerCase().includes(q)
      );
    });
  }, [enriched, search, sourceFilter]);

  const sorted = useMemo(() => {
    const dir = sort.direction === "asc" ? 1 : -1;
    // Stable secondary sort by configName so items with equal primary keys
    // (e.g. same key count) keep a deterministic order.
    return [...filtered].sort((a, b) => {
      let primary = 0;
      switch (sort.field) {
        case "name":
          primary = compareSortString(a.config.configName, b.config.configName);
          break;
        case "app":
          primary = compareSortString(a.appName, b.appName);
          break;
        case "keys":
          primary = a.config.keys.length - b.config.keys.length;
          break;
        case "updated":
          primary = toSortTime(a.config.updatedAt) - toSortTime(b.config.updatedAt);
          break;
      }
      if (primary !== 0) return primary * dir;
      return compareSortString(a.config.configName, b.config.configName);
    });
  }, [filtered, sort]);

  const totalLinked = enriched.filter((e) => e.linked).length;
  const totalExternal = enriched.length - totalLinked;

  const handleEdit = (entry: (typeof enriched)[number]) => {
    navigate(`/configurations/app-config/${entry.config.policyId}/edit`);
  };

  // For now "View" routes to the same wizard as edit. The wizard surfaces
  // every key, value, and tag assignment; a dedicated read-only detail page
  // can be added later if needed.
  const handleView = handleEdit;

  const handleDuplicate = (entry: (typeof enriched)[number]) => {
    navigate(
      `/configurations/app-config/new?duplicate=${entry.config.policyId}`,
    );
  };

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6 space-y-6">
        <PageHeader
          title="App Configuration"
          description="All managed App Configuration policies (key/value) across your catalog and external apps."
        >
          <Button onClick={() => navigate("/configurations/app-config/new")}>
            <Plus className="mr-2 h-4 w-4" />
            New Configuration
          </Button>
        </PageHeader>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by app, bundle ID, or config name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-72"
              />
            </div>

            <Select
              value={sourceFilter}
              onValueChange={(v) => setSourceFilter(v as typeof sourceFilter)}
            >
              <SelectTrigger className="w-44">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="linked">Linked to catalog</SelectItem>
                <SelectItem value="external">External app</SelectItem>
              </SelectContent>
            </Select>

            <SortControls
              field={sort.field}
              direction={sort.direction}
              options={SORT_OPTIONS}
              onFieldChange={setField}
              onDirectionChange={setDirection}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{configs.length}</Badge>
                <span>Total</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{totalLinked}</Badge>
                <span>Linked</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{totalExternal}</Badge>
                <span>External</span>
              </div>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-3">
                <div className="h-5 w-2/3 bg-muted rounded animate-pulse" />
                <div className="h-4 w-full bg-muted rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="border rounded-lg p-12 text-center">
            <Settings className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold">
              {configs.length === 0
                ? "No App Configurations yet"
                : "No matches"}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md mx-auto">
              {configs.length === 0
                ? "Push key/value settings to managed apps. Pick an app from your catalog or target an external app by bundle ID."
                : "Try a different search term or filter."}
            </p>
            {configs.length === 0 && (
              <Button onClick={() => navigate("/configurations/app-config/new")}>
                <Plus className="mr-2 h-4 w-4" />
                New Configuration
              </Button>
            )}
          </div>
        ) : view === "grid" ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sorted.map((entry) => (
              <AppConfigCard
                key={entry.config.policyId}
                config={entry.config}
                appName={entry.appName}
                iconUrl={entry.iconUrl}
                linkedToCatalog={entry.linked}
                onView={() => handleView(entry)}
                onEdit={() => handleEdit(entry)}
                onDuplicate={() => handleDuplicate(entry)}
                onDelete={() => setDeleteTarget(entry.config)}
              />
            ))}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Configuration</TableHead>
                  <TableHead>Bundle ID</TableHead>
                  <TableHead>Keys</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
            {sorted.map((entry) => (
                  <AppConfigRow
                    key={entry.config.policyId}
                    config={entry.config}
                    appName={entry.appName}
                    iconUrl={entry.iconUrl}
                    linkedToCatalog={entry.linked}
                    onView={() => handleView(entry)}
                    onEdit={() => handleEdit(entry)}
                    onDuplicate={() => handleDuplicate(entry)}
                    onDelete={() => setDeleteTarget(entry.config)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>


      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete App Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.configName}"? It
              will be removed from all assigned devices. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteConfig.mutateAsync(deleteTarget.policyId);
                setDeleteTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
